import { hasRoute } from '@common/utils'
import { useRouter } from 'next/router'
import StudentDetailsModal from '@components/trial-class/join/student-details-modal'
import { useState } from 'react'
import { useSessionContext } from '@contexts/session'
import {
  DEFAULT_USER_DETAILS,
  REJECTED_EMAIL_HELLO_BAR_EXCLUSION_GLOBS,
} from '@common/constants'
import { Trans } from '@lingui/macro'
import { isDummyEmail, isPlaceholderPhone } from '@lib/utils/identifier'
type Props = {
  allowedPaths: string[]
}
const UpdateInfoHelloBar = ({ allowedPaths }: Props) => {
  const [isStudentDetailsModalOpen, setIsStudentDetailsModalOpen] = useState(
    false
  )
  const { name, gender, email, phone, rejectedEmail } = useSessionContext()

  const router = useRouter()

  // The placeholder phone is checked alongside the rest because it is the one
  // detail this bar now exists to collect. Without it the condition can read
  // as complete while the only number we hold is a satellite one nobody can
  // ring - a signup that gave us an email has a real address and a real name,
  // so those two say nothing about whether we can reach them.
  const areAllDetailsUpdated =
    !isDummyEmail(email) &&
    gender &&
    name !== DEFAULT_USER_DETAILS.name &&
    !isPlaceholderPhone(phone)

  // An unreachable account ignores the allow list below: the generic nudge is
  // a nice-to-have, so it waits for a page where it is not in the way, but
  // this is broken until the parent fixes it, so they meet the bar wherever
  // they are - bar the pages a bar would be in the way on.
  //
  // `rejectedEmail` is kept forever as a record of what the parent typed, so
  // on its own it cannot say whether the account is still unreachable. The
  // dummy address is what says that, and it goes as soon as they give us a
  // working one - otherwise this bar would keep naming an address they have
  // already replaced.
  //
  // `/trial-class/join` is inside the excluded funnel and still gets the bar:
  // the class is already booked by then, so there is no booking left to
  // interrupt, and the joining link is exactly what will not arrive.
  const nudgeForEmail =
    Boolean(rejectedEmail) &&
    isDummyEmail(email) &&
    (router.pathname === '/trial-class/join' ||
      !hasRoute(router.pathname, REJECTED_EMAIL_HELLO_BAR_EXCLUSION_GLOBS))

  const isAllowedPath = nudgeForEmail || hasRoute(router.pathname, allowedPaths)

  if (isAllowedPath && !areAllDetailsUpdated)
    return (
      <div className="md:top-0 z-10 p-3 lg:text-lg text-center bg-orange text-white font-600 lg:font-700">
        {/* A parent whose address bounced is not missing details, they gave
            us one that does not work - so the generic prompt reads as
            irrelevant to them and tells them nothing about what went wrong. */}
        {nudgeForEmail ? (
          // Lighter, smaller and narrower than the bar sets: this sentence is
          // long, and full-bleed at 18px/700 it reads as one shout. Same
          // reason the address is mono on an inset rather than bold. All
          // overridden here so the generic bar is unchanged.
          <span className="block max-w-4xl mx-auto font-400 lg:text-base">
            <Trans id="trialClass.updateInfoHelloBar.rejectedEmail">
              Your email address (
              <span className="px-1 rounded font-mono bg-orange-800">
                {rejectedEmail}
              </span>
              ) appears to be <strong className="font-700">invalid</strong>.
              Please{' '}
              <button
                className="px-2 py-px mx-1 align-baseline bg-white rounded focus:outline-none text-grey font-600 whitespace-no-wrap hover:bg-grey-100"
                onClick={() => setIsStudentDetailsModalOpen(true)}
              >
                update it now
              </button>{' '}
              to avoid missing important class updates, reminders, and
              notifications.
            </Trans>
          </span>
        ) : (
          <Trans id="trialClass.updateInfoHelloBar.text">
            <button
              className="focus:outline-none underline lg:text-lg font-600 lg:font-700"
              onClick={() => setIsStudentDetailsModalOpen(true)}
            >
              Update personal info
            </button>{' '}
            to get a personalised experience at Codingal.
          </Trans>
        )}
        <StudentDetailsModal
          isOpen={isStudentDetailsModalOpen}
          setIsOpen={setIsStudentDetailsModalOpen}
          isMinimal={true}
          // A parent who clicked this bar came to fix one thing. Asking for
          // grade, date of birth, city, parent name and gender on the way is
          // five more chances to give up before the account is reachable
          // again.
          collectIdentifiersOnly={nudgeForEmail}
        />
      </div>
    )
  return null
}
export default UpdateInfoHelloBar
